import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Coins, Wallet, Clock, Target, CheckCircle2, CircleDollarSign, FileText, Calculator, Trash } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Modal, Field, TextInput, TextArea, PrimaryButton } from "@/components/admin/ui";
import { SalarySlip } from "@/components/admin/SalarySlip";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const labelFromKey = (key) => {
  if (!key) return "—";
  const [y, m] = key.split("-");
  if (!m) return key;
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
};
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

const empty = {
  application_id: "", host_name: "", user_id: "", agency: "", salary_month: new Date().toISOString().slice(0, 7),
  total_live_days: "", total_live_hours: "", avg_daily_live_hours: "", days_4h: "", days_2h: "", total_reward_coins: "",
  target_status: "Not Completed", basic_salary: "", bonus: "", adjustment: "", total_salary: "",
  status: "Unpaid", payment_date: "", notes: "", daily_records: [],
};

const policyChips = [
  { Icon: Target, label: "Required", value: "120 hrs / month" },
  { Icon: Clock, label: "Daily Target", value: "4 hours" },
  { Icon: Coins, label: "Daily Reward", value: "5,000 coins (2 hrs)" },
  { Icon: CircleDollarSign, label: "Salary Date", value: "15th monthly" },
];

export default function ManageSalaries() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [slip, setSlip] = useState(null);
  const [status, setStatus] = useState("All");
  const [month, setMonth] = useState("");
  const [hostId, setHostId] = useState("");

  const { data: approved } = useQuery({ queryKey: ["approved-hosts"], queryFn: async () => (await api.get("/admin/approved-hosts")).data });
  const { data: items } = useQuery({
    queryKey: ["salaries", status, month, hostId],
    queryFn: async () => {
      const params = {};
      if (status !== "All") params.status = status;
      if (month) params.month = month;
      if (hostId) params.user_id = hostId;
      return (await api.get("/admin/salaries", { params })).data;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["salaries"] });
  const save = useMutation({
    mutationFn: async (data) => editing ? api.put(`/admin/salaries/${editing}`, data) : api.post("/admin/salaries", data),
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Salary record updated" : "Salary record added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/salaries/${id}`),
    onSuccess: () => { invalidate(); toast.success("Record deleted"); },
  });

  const openAdd = () => { setForm({ ...empty, daily_records: [] }); setEditing(null); setModal(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r, daily_records: r.daily_records || [] }); setEditing(r.id); setModal(true); };
  const togglePaid = (r) => {
    const next = r.status === "Paid" ? "Unpaid" : "Paid";
    api.put(`/admin/salaries/${r.id}`, { ...r, status: next, payment_date: next === "Paid" && !r.payment_date ? new Date().toISOString().slice(0, 10) : r.payment_date })
      .then(() => { invalidate(); toast.success(`Marked ${next}`); });
  };

  const onPickHost = (appId) => {
    const h = (approved || []).find((a) => a.id === appId);
    if (h) setForm((f) => ({ ...f, application_id: h.id, host_name: h.full_name, user_id: h.user_id, agency: h.agency_name || "" }));
    else setForm((f) => ({ ...f, application_id: "" }));
  };

  // ---- Daily records helpers ----
  const addDay = () => setForm((f) => ({
    ...f,
    daily_records: [...f.daily_records, { date: "", live_hours: "", target_2h: false, target_4h: false, reward_coins: "", notes: "" }],
  }));
  const updateDay = (i, key, val) => setForm((f) => {
    const rows = f.daily_records.map((d, idx) => {
      if (idx !== i) return d;
      const nd = { ...d, [key]: val };
      if (key === "live_hours") {
        const h = num(val);
        nd.target_2h = h >= 2;
        nd.target_4h = h >= 4;
        nd.reward_coins = h >= 2 ? "5000" : "0";
      }
      return nd;
    });
    return { ...f, daily_records: rows };
  });
  const removeDay = (i) => setForm((f) => ({ ...f, daily_records: f.daily_records.filter((_, idx) => idx !== i) }));

  const recalc = () => setForm((f) => {
    const rows = f.daily_records;
    const days = rows.filter((d) => num(d.live_hours) > 0).length;
    const hours = rows.reduce((s, d) => s + num(d.live_hours), 0);
    const d2 = rows.filter((d) => d.target_2h).length;
    const d4 = rows.filter((d) => d.target_4h).length;
    const coins = rows.reduce((s, d) => s + num(d.reward_coins), 0);
    const avg = days ? (hours / days).toFixed(1) : "";
    return {
      ...f,
      total_live_days: days ? String(days) : f.total_live_days,
      total_live_hours: hours ? String(hours) : f.total_live_hours,
      days_2h: String(d2), days_4h: String(d4),
      total_reward_coins: coins ? String(coins) : f.total_reward_coins,
      avg_daily_live_hours: avg || f.avg_daily_live_hours,
      target_status: hours >= 120 ? "Completed" : "Not Completed",
    };
  });

  const autoTotal = () => setForm((f) => ({ ...f, total_salary: String(num(f.basic_salary) + num(f.bonus) + num(f.adjustment)) }));

  const hostOptions = [...new Map((approved || []).map((a) => [a.user_id || a.id, a])).values()];

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Official Host Salary</h1>
          <p className="mt-1 text-slate-400">Record monthly performance, calculate salary and issue salary slips.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-salary-btn"><Plus className="h-5 w-5" /> Add Record</PrimaryButton>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {policyChips.map(({ Icon, label, value }) => (
          <div key={label} className="card-surface rounded-xl p-4">
            <Icon className="h-5 w-5 text-gold" />
            <p className="mt-2 text-sm font-bold text-white">{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div>
          <label className="mb-1 block text-xs text-slate-500">Status</label>
          <div className="flex gap-2">
            {["All", "Unpaid", "Paid"].map((sopt) => (
              <button key={sopt} onClick={() => setStatus(sopt)} data-testid={`salary-filter-${sopt.toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${status === sopt ? "gold-gradient text-obsidian" : "border border-gold/25 text-slate-300 hover:border-gold/60"}`}>
                {sopt === "Unpaid" ? "Pending / Unpaid" : sopt}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="salary-month-filter"
            className="h-10 rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">Host</label>
          <select value={hostId} onChange={(e) => setHostId(e.target.value)} data-testid="salary-host-filter"
            className="h-10 rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60">
            <option value="">All Hosts</option>
            {hostOptions.map((h) => <option key={h.id} value={h.user_id}>{h.full_name} ({h.user_id})</option>)}
          </select>
        </div>
        {(month || hostId || status !== "All") && (
          <button onClick={() => { setMonth(""); setHostId(""); setStatus("All"); }} className="h-10 rounded-lg px-3 text-sm text-gold hover:bg-gold/10">Clear</button>
        )}
      </div>

      {/* Records */}
      <div className="mt-6 space-y-3">
        {(items || []).map((r) => {
          const pct = Math.min(100, Math.round((num(r.total_live_hours) / 120) * 100));
          return (
            <div key={r.id} className="card-surface rounded-xl p-4" data-testid={`salary-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-bold text-slate-100">{r.host_name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "Paid" ? "bg-whatsapp/15 text-whatsapp" : "bg-amber-500/15 text-amber-400"}`}>{r.status}</span>
                    <span className="rounded-full bg-gold/10 px-2 py-0.5 text-xs font-semibold text-gold">{labelFromKey(r.salary_month)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.target_status === "Completed" ? "bg-whatsapp/15 text-whatsapp" : "bg-slate-600/30 text-slate-400"}`}>{r.target_status}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-400">ID {r.user_id || "—"} {r.agency && `· ${r.agency}`}</p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                    <span><Clock className="mr-1 inline h-3 w-3 text-gold" />{r.total_live_hours || "0"}/120 hrs ({pct}%)</span>
                    <span>Days: {r.total_live_days || "0"}</span>
                    <span><Wallet className="mr-1 inline h-3 w-3 text-gold" />Total: {r.total_salary || "—"}</span>
                    <span><Coins className="mr-1 inline h-3 w-3 text-gold" />{r.total_reward_coins || "0"} coins</span>
                    {r.daily_records?.length > 0 && <span>{r.daily_records.length} daily entries</span>}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                  <button onClick={() => setSlip(r)} title="View Salary Slip" data-testid={`view-slip-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-gold/30 px-2.5 py-1.5 text-xs font-bold text-gold hover:bg-gold/10">
                    <FileText className="h-4 w-4" /> Slip
                  </button>
                  <button onClick={() => togglePaid(r)} title="Toggle Paid/Unpaid" data-testid={`salary-toggle-${r.id}`}
                    className={`rounded-lg p-2 ${r.status === "Paid" ? "text-amber-400 hover:bg-amber-500/10" : "text-whatsapp hover:bg-whatsapp/10"}`}>
                    <CircleDollarSign className="h-5 w-5" />
                  </button>
                  <button onClick={() => openEdit(r)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-salary-${r.id}`}><Pencil className="h-5 w-5" /></button>
                  <button onClick={() => { if (window.confirm("Delete this salary record?")) del.mutate(r.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-salary-${r.id}`}><Trash2 className="h-5 w-5" /></button>
                </div>
              </div>
            </div>
          );
        })}
        {items?.length === 0 && (
          <p className="py-12 text-center text-slate-500" data-testid="salary-empty">No salary records yet. Click "Add Record" and pick an approved host.</p>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit Salary Record" : "Add Salary Record"} testid="salary-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-5">
          {!editing && (
            <Field label="Select Approved Host">
              <select value={form.application_id} onChange={(e) => onPickHost(e.target.value)} data-testid="salary-host-select"
                className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60">
                <option value="">-- Choose from approved hosts --</option>
                {(approved || []).map((h) => <option key={h.id} value={h.id}>{h.full_name + " · " + h.user_id}</option>)}
              </select>
              {(!approved || approved.length === 0) && (
                <p className="mt-1 text-xs text-amber-400">No approved hosts yet. Approve applications in Host Applications first.</p>
              )}
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Host Name"><TextInput value={form.host_name} onChange={(e) => setForm((f) => ({ ...f, host_name: e.target.value }))} required testid="salary-name-input" /></Field>
            <Field label="Golden Live User ID"><TextInput value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} testid="salary-userid-input" /></Field>
            <Field label="Agency"><TextInput value={form.agency} onChange={(e) => setForm((f) => ({ ...f, agency: e.target.value }))} testid="salary-agency-input" /></Field>
            <Field label="Salary Month"><TextInput type="month" value={form.salary_month} onChange={(e) => setForm((f) => ({ ...f, salary_month: e.target.value }))} testid="salary-month-input" /></Field>
          </div>

          {/* Daily performance */}
          <div className="rounded-xl border border-gold/15 bg-obsidian p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-base gold-text">Daily Performance</p>
              <div className="flex gap-2">
                <button type="button" onClick={addDay} data-testid="add-daily-btn" className="inline-flex items-center gap-1 rounded-lg border border-gold/30 px-3 py-1.5 text-xs font-bold text-gold hover:bg-gold/10"><Plus className="h-4 w-4" /> Add Day</button>
                <button type="button" onClick={recalc} data-testid="recalc-btn" className="inline-flex items-center gap-1 rounded-lg gold-gradient px-3 py-1.5 text-xs font-bold text-obsidian"><Calculator className="h-4 w-4" /> Recalculate</button>
              </div>
            </div>
            {form.daily_records.length === 0 && <p className="py-3 text-center text-xs text-slate-500">No daily entries. Add days, then Recalculate to auto-fill the monthly summary.</p>}
            <div className="space-y-2">
              {form.daily_records.map((d, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`daily-row-${i}`}>
                  <input type="date" value={d.date} onChange={(e) => updateDay(i, "date", e.target.value)} data-testid={`daily-date-${i}`}
                    className="col-span-4 h-9 rounded-lg border border-gold/20 bg-obsidian-card px-2 text-xs text-slate-100 outline-none focus:border-gold/60 sm:col-span-3" />
                  <input type="number" step="0.1" placeholder="hrs" value={d.live_hours} onChange={(e) => updateDay(i, "live_hours", e.target.value)} data-testid={`daily-hours-${i}`}
                    className="col-span-2 h-9 rounded-lg border border-gold/20 bg-obsidian-card px-2 text-xs text-slate-100 outline-none focus:border-gold/60" />
                  <label className="col-span-2 flex items-center gap-1 text-xs text-slate-300 sm:col-span-1"><input type="checkbox" checked={d.target_2h} onChange={(e) => updateDay(i, "target_2h", e.target.checked)} className="accent-[#D4AF37]" />2h</label>
                  <label className="col-span-2 flex items-center gap-1 text-xs text-slate-300 sm:col-span-1"><input type="checkbox" checked={d.target_4h} onChange={(e) => updateDay(i, "target_4h", e.target.checked)} className="accent-[#D4AF37]" />4h</label>
                  <input type="number" placeholder="coins" value={d.reward_coins} onChange={(e) => updateDay(i, "reward_coins", e.target.value)} data-testid={`daily-coins-${i}`}
                    className="col-span-2 h-9 rounded-lg border border-gold/20 bg-obsidian-card px-2 text-xs text-slate-100 outline-none focus:border-gold/60 sm:col-span-2" />
                  <input placeholder="note" value={d.notes} onChange={(e) => updateDay(i, "notes", e.target.value)}
                    className="col-span-10 h-9 rounded-lg border border-gold/20 bg-obsidian-card px-2 text-xs text-slate-100 outline-none focus:border-gold/60 sm:col-span-3" />
                  <button type="button" onClick={() => removeDay(i)} data-testid={`daily-remove-${i}`} className="col-span-2 flex justify-center text-red-400 hover:text-red-300 sm:col-span-1"><Trash className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly performance summary */}
          <div className="rounded-xl border border-gold/15 bg-obsidian p-4">
            <p className="mb-3 font-display text-base gold-text">Monthly Performance</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Total Live Days"><TextInput value={form.total_live_days} onChange={(e) => setForm((f) => ({ ...f, total_live_days: e.target.value }))} testid="salary-days-input" /></Field>
              <Field label="Total Live Hours"><TextInput value={form.total_live_hours} onChange={(e) => setForm((f) => ({ ...f, total_live_hours: e.target.value }))} testid="salary-totalhours-input" /></Field>
              <Field label="Avg Daily Hours"><TextInput value={form.avg_daily_live_hours} onChange={(e) => setForm((f) => ({ ...f, avg_daily_live_hours: e.target.value }))} testid="salary-avg-input" /></Field>
              <Field label="Days 4+ hrs"><TextInput value={form.days_4h} onChange={(e) => setForm((f) => ({ ...f, days_4h: e.target.value }))} testid="salary-days4-input" /></Field>
              <Field label="Days 2+ hrs"><TextInput value={form.days_2h} onChange={(e) => setForm((f) => ({ ...f, days_2h: e.target.value }))} testid="salary-days2-input" /></Field>
              <Field label="Total Reward Coins"><TextInput value={form.total_reward_coins} onChange={(e) => setForm((f) => ({ ...f, total_reward_coins: e.target.value }))} testid="salary-coins-input" /></Field>
            </div>
            <Field label="Monthly Target Status">
              <select value={form.target_status} onChange={(e) => setForm((f) => ({ ...f, target_status: e.target.value }))} data-testid="salary-target-input"
                className="mt-1 h-11 w-full rounded-lg border border-gold/20 bg-obsidian-card px-3 text-slate-100 outline-none focus:border-gold/60">
                <option>Not Completed</option>
                <option>Completed</option>
              </select>
            </Field>
          </div>

          {/* Salary */}
          <div className="rounded-xl border border-gold/15 bg-obsidian p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-base gold-text">Salary</p>
              <button type="button" onClick={autoTotal} data-testid="auto-total-btn" className="inline-flex items-center gap-1 rounded-lg border border-gold/30 px-3 py-1.5 text-xs font-bold text-gold hover:bg-gold/10"><Calculator className="h-4 w-4" /> Auto Total</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Basic Salary"><TextInput value={form.basic_salary} onChange={(e) => setForm((f) => ({ ...f, basic_salary: e.target.value }))} placeholder="e.g. 200" testid="salary-basic-input" /></Field>
              <Field label="Bonus"><TextInput value={form.bonus} onChange={(e) => setForm((f) => ({ ...f, bonus: e.target.value }))} testid="salary-bonus-input" /></Field>
              <Field label="Other Adjustment"><TextInput value={form.adjustment} onChange={(e) => setForm((f) => ({ ...f, adjustment: e.target.value }))} testid="salary-adjustment-input" /></Field>
              <Field label="Total Salary"><TextInput value={form.total_salary} onChange={(e) => setForm((f) => ({ ...f, total_salary: e.target.value }))} testid="salary-total-input" /></Field>
              <Field label="Payment Status">
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} data-testid="salary-status-input"
                  className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian-card px-3 text-slate-100 outline-none focus:border-gold/60">
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                </select>
              </Field>
              <Field label="Payment Date"><TextInput type="date" value={form.payment_date} onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} testid="salary-paydate-input" /></Field>
            </div>
          </div>

          <Field label="Admin Notes"><TextArea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} testid="salary-notes-input" /></Field>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-salary-btn">{save.isPending ? "Saving..." : "Save Record"}</PrimaryButton>
          </div>
        </form>
      </Modal>

      {slip && <SalarySlip record={slip} onClose={() => setSlip(null)} />}
    </div>
  );
}
